import { Undo2, ChevronDown, CreditCard } from "lucide-react";
import type { Charge } from "@/components/admin/_mockData";

/**
 * Refund dialog for a Stripe charge. Charge picker (preselected), amount
 * input with full/50%/custom chips, Stripe-API reason code chips,
 * internal note. ROADS-476 deliverable C2.
 */
interface RefundModalProps {
  charge: Charge;
  open: boolean;
  onCancel: () => void;
  onConfirm?: (amountUsd: number, reasonCode: string, note: string) => void;
}

export default function RefundModal({
  charge,
  open,
  onCancel,
}: RefundModalProps) {
  if (!open) return null;
  const reasons: [string, boolean][] = [
    ["duplicate", false],
    ["fraudulent", false],
    ["requested_by_customer", true],
    ["other", false],
  ];
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="refund-modal-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-6"
      data-testid="admin-refund-modal"
    >
      <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start gap-3.5 border-b border-indigo-200 bg-indigo-50 px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600">
            <Undo2 aria-hidden="true" className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2
              id="refund-modal-title"
              className="text-lg font-bold text-indigo-800"
            >
              Refund charge via Stripe
            </h2>
            <p className="text-xs font-medium text-indigo-800">
              Sends refund through stripe.refunds.create. Customer may take
              5–10 business days.
            </p>
          </div>
        </div>
        <div className="space-y-4 p-5">
          <div>
            <label className="block text-[11px] font-semibold text-slate-700">
              Charge
            </label>
            <div className="mt-1.5 flex items-center gap-3.5 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
              <div
                aria-hidden="true"
                className="flex h-9 w-9 items-center justify-center rounded-lg"
                style={{ backgroundColor: "#635BFF" }}
              >
                <CreditCard aria-hidden="true" className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-slate-900">
                  ${charge.amountUsd.toFixed(2)} · {charge.description}
                </div>
                <div className="truncate font-mono text-[11px] text-slate-500">
                  {charge.date} · {charge.id} · {charge.method}
                </div>
              </div>
              <ChevronDown
                aria-hidden="true"
                className="w-3.5 h-3.5 text-slate-500"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="refund-amount"
              className="block text-[11px] font-semibold text-slate-700"
            >
              Refund amount
            </label>
            <div className="mt-1.5 flex items-center gap-2.5">
              <div className="flex flex-1 items-center gap-2.5 rounded-lg border border-slate-300 bg-white px-3.5 py-3">
                <span className="text-base font-semibold text-slate-500">$</span>
                <input
                  id="refund-amount"
                  type="text"
                  defaultValue={charge.amountUsd.toFixed(2)}
                  className="flex-1 bg-transparent font-mono text-base font-semibold text-slate-900 focus:outline-none"
                />
                <span className="text-[11px] font-semibold text-slate-500">
                  USD
                </span>
              </div>
              <div className="flex gap-1.5">
                {[
                  ["Full", true],
                  ["50%", false],
                  ["Custom", false],
                ].map(([label, active]) => (
                  <button
                    key={String(label)}
                    type="button"
                    className={`rounded-lg border px-3.5 py-2 text-xs font-semibold ${
                      active
                        ? "border-indigo-600 bg-indigo-600 text-white"
                        : "border-slate-200 bg-white text-slate-700"
                    }`}
                  >
                    {String(label)}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-700">
              Reason code · passed to Stripe + audit log
            </label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {reasons.map(([code, active]) => (
                <button
                  key={code}
                  type="button"
                  className={`rounded-full border px-3 py-1.5 font-mono text-[11px] font-semibold ${
                    active
                      ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                      : "border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label
              htmlFor="refund-note"
              className="block text-[11px] font-semibold text-slate-700"
            >
              Internal note (audit only)
            </label>
            <textarea
              id="refund-note"
              className="mt-1.5 w-full rounded-lg border border-slate-200 p-3 text-xs text-slate-900"
              defaultValue="User reported app crash mid-cycle, refunding the month per CS goodwill policy."
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2.5 border-t border-slate-200 bg-slate-50 px-5 py-3.5">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700"
          >
            <Undo2 aria-hidden="true" className="w-3.5 h-3.5" />
            Refund ${charge.amountUsd.toFixed(2)}
          </button>
        </div>
      </div>
    </div>
  );
}
